import config from "@/libs/config";
import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserPool,
  CognitoUserSession,
  ICognitoUserSessionData,
  CognitoAccessToken,
  CognitoIdToken,
  CognitoRefreshToken,
} from "amazon-cognito-identity-js";

const poolData = {
  UserPoolId: config.USER_POOL_ID,
  ClientId: config.COGNITO_CLIENT_ID,
};

const userPool: CognitoUserPool = new CognitoUserPool(poolData);

let currentUser: CognitoUser = userPool.getCurrentUser();

export function getCurrentUser() {
  return currentUser;
}

function getCognitoUser(username: string) {
  const userData = {
    Username: username,
    Pool: userPool,
  };
  const cognitoUser = new CognitoUser(userData);

  return cognitoUser;
}

export async function getSession() {
  if (!currentUser) {
    currentUser = userPool.getCurrentUser();
  }

  return new Promise(function (resolve, reject) {
    currentUser.getSession(function (err: any, session: any) {
      if (err) {
        reject(err);
      } else {
        resolve(session);
      }
    });
  }).catch((err) => {
    throw err;
  });
}

export async function verifyCode(username: string, code: string) {
  return new Promise(function (resolve, reject) {
    const cognitoUser = getCognitoUser(username);

    cognitoUser.confirmRegistration(code, true, function (err, result) {
      if (err) {
        reject(err);
      } else {
        resolve(result);
      }
    });
  }).catch((err) => {
    throw err;
  });
}

export class NewPasswordRequired {}

export async function signIn(username: string, password: string) {
  return new Promise(function (resolve, reject) {
    const authenticationData = {
      Username: username,
      Password: password,
    };
    const authenticationDetails = new AuthenticationDetails(authenticationData);

    currentUser = getCognitoUser(username);

    currentUser.authenticateUser(authenticationDetails, {
      onSuccess: function (res: any) {
        resolve(res);
      },
      onFailure: function (err: any) {
        reject(err);
      },
      newPasswordRequired: function (userAttributes, requiredAttributes) {
        resolve(new NewPasswordRequired());
      },
    });
  }).catch((err) => {
    throw err;
  });
}

export function signOut(callback?: () => void): void {
  if (currentUser) {
    currentUser.signOut(callback);
  }
}

export async function sendCode(username: string) {
  return new Promise(function (resolve, reject) {
    const cognitoUser = getCognitoUser(username);

    if (!cognitoUser) {
      reject(`could not find ${username}`);
      return;
    }

    cognitoUser.forgotPassword({
      onSuccess: function (res) {
        resolve(res);
      },
      onFailure: function (err) {
        reject(err);
      },
    });
  }).catch((err) => {
    throw err;
  });
}

export async function forgotPassword(
  username: string,
  code: string,
  password: string
) {
  return new Promise(function (resolve, reject) {
    const cognitoUser = getCognitoUser(username);

    if (!cognitoUser) {
      reject(`could not find ${username}`);
      return;
    }

    cognitoUser.confirmPassword(code, password, {
      onSuccess: function () {
        resolve("password updated");
      },
      onFailure: function (err) {
        reject(err);
      },
    });
  });
}

export async function changePassword(oldPassword: string, newPassword: string) {
  return new Promise(function (resolve, reject) {
    currentUser.changePassword(
      oldPassword,
      newPassword,
      function (err: any, res: any) {
        if (err) {
          reject(err);
        } else {
          resolve(res);
        }
      }
    );
  });
}

export async function completeNewPasswordChallenge(newPassword: string) {
  return new Promise(function (resolve, reject) {
    currentUser.completeNewPasswordChallenge(
      newPassword,
      {},
      {
        onSuccess: function (res: any) {
          resolve(res);
        },
        onFailure: function (err: any) {
          reject(err);
        },
      }
    );
  });
}

export async function setSignInUserSession(session: {
  AccessToken: string;
  IdToken: string;
  RefreshToken: string;
}) {
  const AccessToken = new CognitoAccessToken(session);
  const IdToken = new CognitoIdToken(session);
  const RefreshToken = new CognitoRefreshToken(session);

  const data: ICognitoUserSessionData = {
    AccessToken: AccessToken,
    IdToken: IdToken,
    RefreshToken: RefreshToken,
  };
  const userSession = new CognitoUserSession(data);

  const username = IdToken.decodePayload()["cognito:username"];
  currentUser = getCognitoUser(username);

  currentUser.setSignInUserSession(userSession);
}
